// src/components/calendar/VisitDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar as DatePicker } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { Dropdown } from 'primereact/dropdown';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { confirmDialog } from 'primereact/confirmdialog';

const VisitDialog = ({ visible, onHide, onSaveVisit, onDeleteVisit, eventColors, initialData, viewMode }) => {
    const defaultVisitData = {
        id: null,
        title: '',
        arrival: new Date(),
        departure: new Date(new Date().getTime() + (3 * 24 * 60 * 60 * 1000)),
        allDay: false,
        description: '',
        backgroundColor: '#1976D2',
        linkedTodoId: null,
        repeat: false,
        repeatType: 'daily',
        repeatUntil: null,
        parentEventId: null,
        reminders: {
            oneWeekBefore: false,
            twoDaysBefore: false
        },
        dailyPlans: [],
        tickets: []
    };

    const [visitData, setVisitData] = useState(defaultVisitData);
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        if (visible) {
            if (initialData) {
                setVisitData(initialData);
            } else {
                setVisitData(defaultVisitData);
            }
            setEditMode(!viewMode);
        }
    }, [visible, initialData, viewMode]);

    useEffect(() => {
        if (!editMode) return;

        if (visitData.arrival && visitData.departure) {
            const daysDiff = Math.max(1, Math.ceil((visitData.departure - visitData.arrival) / (1000 * 60 * 60 * 24)));
            const days = [];

            for (let i = 0; i < daysDiff; i++) {
                const day = new Date(visitData.arrival);
                day.setDate(day.getDate() + i);

                const existingPlan = visitData.dailyPlans.find(plan =>
                    new Date(plan.date).toDateString() === day.toDateString()
                );

                days.push(existingPlan || {
                    date: day,
                    notes: '',
                    bulletPoints: ['']
                });
            }

            setVisitData(prev => ({...prev, dailyPlans: days}));
        }
    }, [visitData.arrival, visitData.departure, editMode]);

    const handleSaveVisit = () => {
        const eventData = {
            ...visitData,
            start: visitData.arrival,
            end: visitData.departure,
            title: visitData.title || 'Visit',
            description: `${visitData.description}\n\nArrival: ${visitData.arrival.toLocaleString()}\nDeparture: ${visitData.departure.toLocaleString()}`
        };

        onSaveVisit(eventData);
        onHide();
    };

    const confirmDelete = () => {
        confirmDialog({
            message: 'Are you sure you want to delete this visit?',
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => {
                if (onDeleteVisit && visitData.id) {
                    onDeleteVisit(visitData.id);
                    onHide();
                }
            }
        });
    };

    const handleAddBulletPoint = (dayIndex) => {
        const updatedPlans = [...visitData.dailyPlans];
        updatedPlans[dayIndex].bulletPoints.push('');
        setVisitData({...visitData, dailyPlans: updatedPlans});
    };

    const handleBulletPointChange = (dayIndex, bulletIndex, value) => {
        const updatedPlans = [...visitData.dailyPlans];
        updatedPlans[dayIndex].bulletPoints[bulletIndex] = value;
        setVisitData({...visitData, dailyPlans: updatedPlans});
    };

    const handleRemoveBulletPoint = (dayIndex, bulletIndex) => {
        const updatedPlans = [...visitData.dailyPlans];
        updatedPlans[dayIndex].bulletPoints.splice(bulletIndex, 1);
        setVisitData({...visitData, dailyPlans: updatedPlans});
    };

    const onTicketUpload = (event) => {
        const tickets = [...visitData.tickets];
        const files = event.files;

        for (let file of files) {
            tickets.push({
                name: file.name,
                size: file.size,
                type: file.type,
                objectURL: URL.createObjectURL(file)
            });
        }

        setVisitData({...visitData, tickets});
    };

    const dialogFooter = viewMode && !editMode ? (
        <>
            <Button label="Close" icon="pi pi-times" className="p-button-text" onClick={onHide} />
            {visitData.id && <Button label="Delete" icon="pi pi-trash" className="p-button-text p-button-danger" onClick={confirmDelete} />}
            <Button label="Edit" icon="pi pi-pencil" onClick={() => setEditMode(true)} />
        </>
    ) : (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={onHide} />
            {visitData.id && <Button label="Delete" icon="pi pi-trash" className="p-button-text p-button-danger" onClick={confirmDelete} />}
            <Button label="Save Visit" icon="pi pi-check" onClick={handleSaveVisit} />
        </>
    );

    if (viewMode && !editMode) {
        return (
            <Dialog
                visible={visible}
                header={`Visit: ${visitData.title || 'Untitled Visit'}`}
                modal
                className="plan-visit-dialog-custom frazza-dialog-lg"
                footer={dialogFooter}
                onHide={onHide}
            >
                <div className="vd-view-content">
                    <div className="vd-view-header">
                        <div className="vd-dates-container">
                            <div className="vd-date-item">
                                <i className="pi pi-calendar-plus"></i>
                                <div>
                                    <strong>Arrival</strong>
                                    <div>{visitData.arrival.toLocaleString()}</div>
                                </div>
                            </div>
                            <div className="vd-date-item">
                                <i className="pi pi-calendar-minus"></i>
                                <div>
                                    <strong>Departure</strong>
                                    <div>{visitData.departure.toLocaleString()}</div>
                                </div>
                            </div>
                        </div>

                        {visitData.description && (
                            <div className="vd-description">
                                <h3>Description</h3>
                                <p>{visitData.description}</p>
                            </div>
                        )}
                    </div>

                    <div className="vd-reminders">
                        <h3>Reminders</h3>
                        {!visitData.reminders.oneWeekBefore && !visitData.reminders.twoDaysBefore ? (
                            <p>No reminders set</p>
                        ) : (
                            <ul>
                                {visitData.reminders.oneWeekBefore && <li>One week before</li>}
                                {visitData.reminders.twoDaysBefore && <li>Two days before</li>}
                            </ul>
                        )}
                    </div>

                    {visitData.tickets && visitData.tickets.length > 0 && (
                        <div className="vd-tickets">
                            <h3>Tickets</h3>
                            <div className="vd-ticket-list">
                                {visitData.tickets.map((ticket, index) => (
                                    <div key={index} className="vd-ticket-item">
                                        <i className="pi pi-ticket"></i>
                                        <span>{ticket.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {visitData.dailyPlans && visitData.dailyPlans.length > 0 && (
                        <div className="vd-daily-plans">
                            <h3>Daily Plans</h3>
                            <Accordion>
                                {visitData.dailyPlans.map((day, dayIndex) => (
                                    <AccordionTab
                                        key={dayIndex}
                                        header={`Day ${dayIndex + 1} - ${new Date(day.date).toLocaleDateString()}`}
                                    >
                                        {day.notes && (
                                            <div className="vd-day-notes">
                                                <p>{day.notes}</p>
                                            </div>
                                        )}

                                        {day.bulletPoints && day.bulletPoints.length > 0 && day.bulletPoints.some(point => point) && (
                                            <div className="vd-day-activities">
                                                <h4>Activities</h4>
                                                <ul>
                                                    {day.bulletPoints.filter(point => point).map((point, idx) => (
                                                        <li key={idx}>{point}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </AccordionTab>
                                ))}
                            </Accordion>
                        </div>
                    )}
                </div>
            </Dialog>
        );
    }

    return (
        <Dialog
            visible={visible}
            header={visitData.id ? 'Edit Visit' : 'Plan a Visit'}
            modal
            className="plan-visit-dialog-custom frazza-dialog-lg"
            footer={dialogFooter}
            onHide={onHide}
        >
            <div className="vd-grid">
                <div className="vd-col-full">
                    <div className="frazza-form-group">
                        <label htmlFor="visitTitle" className="frazza-form-label required">Visit Title</label>
                        <InputText
                            id="visitTitle"
                            value={visitData.title}
                            onChange={(e) => setVisitData({...visitData, title: e.target.value})}
                            placeholder="e.g., Trip to Paris"
                            autoFocus
                            className="frazza-input"
                        />
                    </div>
                </div>

                <div className="vd-col-half">
                    <div className="frazza-form-group">
                        <label htmlFor="arrivalDate" className="frazza-form-label">Arrival</label>
                        <DatePicker
                            id="arrivalDate"
                            value={visitData.arrival}
                            onChange={(e) => setVisitData({...visitData, arrival: e.value})}
                            showTime
                            dateFormat="dd/mm/yy"
                            stepMinute={15}
                            hourFormat="12"
                            className="frazza-input"
                        />
                    </div>
                </div>

                <div className="vd-col-half">
                    <div className="frazza-form-group">
                        <label htmlFor="departureDate" className="frazza-form-label">Departure</label>
                        <DatePicker
                            id="departureDate"
                            value={visitData.departure}
                            onChange={(e) => setVisitData({...visitData, departure: e.value})}
                            showTime
                            dateFormat="dd/mm/yy"
                            stepMinute={15}
                            hourFormat="12"
                            className="frazza-input"
                            minDate={visitData.arrival}
                        />
                    </div>
                </div>

                <div className="vd-col-full">
                    <div className="frazza-form-group">
                        <label htmlFor="description" className="frazza-form-label">Description</label>
                        <InputTextarea
                            id="description"
                            value={visitData.description}
                            onChange={(e) => setVisitData({...visitData, description: e.target.value})}
                            rows={3}
                            className="frazza-textarea"
                            placeholder="Details about your visit..."
                        />
                    </div>
                </div>

                <div className="vd-col-full">
                    <div className="frazza-form-group">
                        <label htmlFor="color" className="frazza-form-label">Color</label>
                        <Dropdown
                            id="color"
                            value={visitData.backgroundColor}
                            options={eventColors}
                            onChange={(e) => setVisitData({...visitData, backgroundColor: e.value})}
                            optionLabel="name"
                            optionValue="value"
                            className="frazza-select"
                        />
                    </div>
                </div>

                <div className="vd-col-full">
                    <div className="frazza-form-group">
                        <label className="frazza-form-label">Reminders</label>
                        <div className="vd-checkbox-group">
                            <div className="frazza-checkbox-item">
                                <Checkbox
                                    inputId="oneWeek"
                                    checked={visitData.reminders.oneWeekBefore}
                                    onChange={(e) => setVisitData({
                                        ...visitData,
                                        reminders: {...visitData.reminders, oneWeekBefore: e.checked}
                                    })}
                                />
                                <label htmlFor="oneWeek" className="frazza-checkbox-label">One week before</label>
                            </div>
                            <div className="frazza-checkbox-item">
                                <Checkbox
                                    inputId="twoDays"
                                    checked={visitData.reminders.twoDaysBefore}
                                    onChange={(e) => setVisitData({
                                        ...visitData,
                                        reminders: {...visitData.reminders, twoDaysBefore: e.checked}
                                    })}
                                />
                                <label htmlFor="twoDays" className="frazza-checkbox-label">Two days before</label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="vd-col-full">
                    <div className="frazza-form-group">
                        <label className="frazza-form-label">Tickets</label>
                        <FileUpload
                            name="tickets"
                            url="./upload"
                            multiple
                            accept="image/*,application/pdf"
                            maxFileSize={5000000}
                            onUpload={onTicketUpload}
                            emptyTemplate={<p className="vd-upload-placeholder">Drag and drop tickets here to upload.</p>}
                        />
                    </div>
                </div>

                <div className="vd-col-full vd-daily-plans-section">
                    <div className="frazza-form-group">
                        <label className="frazza-form-label">Daily Planning</label>
                        <Accordion>
                            {visitData.dailyPlans.map((day, dayIndex) => (
                                <AccordionTab
                                    key={dayIndex}
                                    header={`Day ${dayIndex + 1} - ${new Date(day.date).toLocaleDateString()}`}
                                >
                                    <InputTextarea
                                        value={day.notes}
                                        onChange={(e) => {
                                            const updatedPlans = [...visitData.dailyPlans];
                                            updatedPlans[dayIndex].notes = e.target.value;
                                            setVisitData({...visitData, dailyPlans: updatedPlans});
                                        }}
                                        rows={2}
                                        placeholder="Notes for this day..."
                                        className="frazza-textarea vd-day-notes-input"
                                    />

                                    <label className="frazza-form-label vd-activities-label">Activities</label>
                                    {day.bulletPoints.map((point, bulletIndex) => (
                                        <div key={bulletIndex} className="vd-bullet-point">
                                            <span className="vd-bullet-marker">•</span>
                                            <InputText
                                                value={point}
                                                onChange={(e) => handleBulletPointChange(dayIndex, bulletIndex, e.target.value)}
                                                placeholder="Activity"
                                                className="frazza-input vd-bullet-input"
                                            />
                                            <Button
                                                icon="pi pi-times"
                                                className="p-button-rounded p-button-text p-button-sm vd-remove-bullet"
                                                onClick={() => handleRemoveBulletPoint(dayIndex, bulletIndex)}
                                            />
                                        </div>
                                    ))}
                                    <Button
                                        label="Add Activity"
                                        icon="pi pi-plus"
                                        className="p-button-text p-button-sm vd-add-bullet"
                                        onClick={() => handleAddBulletPoint(dayIndex)}
                                    />
                                </AccordionTab>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default VisitDialog;